import _ from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { from, Observable } from 'rxjs';
import { filter, map, toArray } from 'rxjs/operators';
import { HssrParams } from './HssrPage.metadata';
import { Language } from './language';

export interface UnauthenticatedHssrFormProps {
    memberData: HssrParams;
    language: Language;
    onFormSubmitted: () => void;
}

export interface SubmitResponse {
    success: boolean;
    message: string;
    caseNo: string;
}

export interface HssrForm {
    memberId: string;
    answer1: PredefinedAnswer<string>;
    answer2: PredefinedAnswer<string>;
    answer3: PredefinedAnswer<string>;
    answer4: YesOrNoOption;
    answer5: boolean;
    dirtyFields: AnswerField[];
    requiredFields: OptionalField[];
}

type OptionalField = ExtractType<keyof HssrParams, 'answer1' | 'answer2' | 'answer3'>;
const OptionalFields: OptionalField[] = ['answer1', 'answer2', 'answer3'];
export const DefaultHssrForm = (data: HssrParams): HssrForm => {
    const isOptionalField = (object: unknown): object is OptionalField =>
        OptionalFields.includes(object as OptionalField);
    const configuredFieldsForMember = Object
        .keys(data)
        .filter(isOptionalField)
        .reduce((acc: OptionalField[], curr: OptionalField, _: number): OptionalField[] => {
            const isRequiredForMember = data[curr];
            return isRequiredForMember ? [...acc, curr] : acc;
        }, []);
    return ({
        memberId: data.memberId,
        answer1: DefaultPredefinedAnswer(),
        answer2: DefaultPredefinedAnswer(),
        answer3: DefaultPredefinedAnswer(),
        answer4: '',
        answer5: false,
        dirtyFields: [],
        requiredFields: configuredFieldsForMember,
    });
};

export type HssrFormProcessor = (f: HssrForm) => HssrForm;

export type HssrKey = keyof HssrForm;
export type HssrValue<k extends HssrKey> = HssrForm[k];
type ChangeSet = [HssrKey, HssrValue<HssrKey>];
const ApplyChange = (changeSet: ChangeSet): HssrFormProcessor =>
    (f) => ({
        ...cloneDeep(f),
        [changeSet[0]]: changeSet[1],
    });

export const GetPipeline =
    (...changeSet: ChangeSet[]) =>
        (form: HssrForm): Observable<HssrForm> => {
            let pipeline2 = from([form]);
            for (const c of changeSet) {
                pipeline2 = pipeline2.pipe(map(ApplyChange(c)));
            }
            return pipeline2;
        };

type ExtractType<T, U extends T> = T extends U ? T : never;
export type AnswerField = ExtractType<keyof HssrForm, 'answer1' | 'answer2' | 'answer3' | 'answer4' | 'answer5'>;

export interface PredefinedAnswer<tAnswer extends string, > {
    month: string;
    year: string;
    response: tAnswer | '',
}

const DefaultPredefinedAnswer = <tAnswer extends string, >(): PredefinedAnswer<tAnswer> => ({
    response: '',
    month: '',
    year: '',
});

export interface ComposedAnswer<tField extends string> {
    month: string;
    year: string;
    response?: { [key in tField]?: string };
}
export const BloodPressureFields = ['SystolicPressure', 'DiastolicPressure'] as const satisfies readonly string[];
export type BloodPressureField = typeof BloodPressureFields[number];

const DefaultComposedAnswer = (): ComposedAnswer<BloodPressureField> => ({
    response: {
        SystolicPressure: '',
        DiastolicPressure: '',
    },
    month: '',
    year: '',
});

export const YesNoOptions = [
    'Yes',
    'No',
    '',
] as const satisfies readonly string[];
export type YesOrNoOption = typeof YesNoOptions[number];


export const GetYearsForAnswer2 = (option: string, language: Language) => {
    switch (option) {
        case language.colonoscopy:
            return _.range(2015, 2025, 1).map((year) => year.toString());
        case language.stoolTest:
            return ['2024'];
        case language.cologuard:
            return ['2022', '2023', '2024'];
        default:
            return [];
    }
};
export const ToggleItem = (
    array: AnswerField[],
    item: AnswerField,
    checked: boolean,
): AnswerField[] => {
    const tmp = array?.length > 0 ? [...array] : [];
    const splice = (i: string) => tmp.splice(tmp.indexOf(i as AnswerField), 1);
    const proceed = checked ? !tmp.includes(item) : tmp.includes(item);
    if (proceed) {
        checked ? tmp.push(item) : splice(item);
    }
    return tmp.sort();
};

type FormValidator = (form: null | undefined | HssrForm) => boolean;

export const isEmptyString = (value: string): boolean => value.trim().length === 0;
export const isInvalidDate = (value: string): boolean => {
    // expects a valid date string of MM/YYYY
    const regex = /((0[1-9])|(1[0-2]))\/(\d{4})/;
    const matchList = value.match(regex);
    const invalidDate = !matchList || matchList.length === 0;
    return isEmptyString(value) || invalidDate;
};

export const isInvalidBloodPressure = (value: string): boolean => {
    // expects a valid date string of MM/YYYY
    const regex = /(\d{3}|\d{2})\/(\d{3}|\d{2})/;
    const matchList = value.match(regex);
    const invalidReading = !matchList || matchList.length === 0;
    return isEmptyString(value) || invalidReading;
};

export const isEmptyArray = (answer: string[]): boolean => answer.length === 0;

export const IsDirtyArrayAnswer = <
    key extends keyof HssrForm & AnswerField,
    field extends keyof HssrForm[key]>(
    answer: key,
    field: field,
    isDefaultValue: (field: HssrForm[key][field]) => boolean,
): FormValidator => (form) => !!form?.dirtyFields?.includes(answer) && isDefaultValue(form[answer][field]);

export const IsDirtyValueAnswer = <
    key extends keyof HssrForm & AnswerField>(
    answer: key,
    isDefaultValue: (field: HssrForm[key]) => boolean,
): FormValidator => (form) => !!form?.dirtyFields?.includes(answer) && isDefaultValue(form[answer]);

const ValidPredefinedAnswer = <tAnswer extends string, >(
    answer: PredefinedAnswer<tAnswer>,
    shouldValidateDay: boolean = false) => {
    const hasValue = !isEmptyString(answer.response);
    const hasMonth = !isEmptyString(answer.month);
    const hasYear = !isEmptyString(answer.year);
    return hasValue && hasMonth && hasYear;
};

const ValidStringAnswer = <tAnswer extends string, >(answer: tAnswer) => answer.trim().length > 0;
type FieldValidator = (form: HssrForm) => AnswerField | true;
const GetFieldValidator =
    <key extends AnswerField>(
        field: key,
        isValidAnswer: (v: HssrForm[key]) => boolean,
    ): FieldValidator =>
        (form: HssrForm) =>
            isValidAnswer(form[field]) || field;

export const GetInvalidAnswers = (form: HssrForm): Observable<AnswerField[]> => {
    const isAnswerField = (object: unknown): object is AnswerField => (object as AnswerField)?.length > 0;
    const isOptionalField = (object: string): object is OptionalField =>
        OptionalFields.includes(object as OptionalField);
    const isRequired = (f: HssrForm) =>
        (field: AnswerField): boolean => isOptionalField(field)
            ? f.requiredFields.includes(field)
            : true;
    // we get ('true' for valid fields) or (the invalid field name)
    const formValidationResults: Observable<true | AnswerField> = from([
        GetFieldValidator('answer1', (answer) =>
            answer.response === 'No' || ValidPredefinedAnswer(answer))(form),
        GetFieldValidator('answer2', (answer) => {
            const shouldValidateDay = answer.response === 'Cologuard (between 01/01/2022 and 12/31/2024)';
            return answer.response === 'No' || ValidPredefinedAnswer(answer, shouldValidateDay);
        })(form),
        GetFieldValidator('answer3', (answer) =>
            !isInvalidBloodPressure(answer.response) &&
            ValidPredefinedAnswer(answer))(form),
        GetFieldValidator('answer4', (answer) => ValidStringAnswer(answer))(form),
        GetFieldValidator('answer5', (answer) => answer)(form),
    ]);
    return formValidationResults.pipe(
        filter(isAnswerField),
        filter(isRequired(form)),
        toArray(),
    );
};